/**
 * Flow Pane Indicator (intraday net dealer options flow time series;
 * displayed as the "Flow pane" sub-toggle of Options Volume)
 *
 * Renders cumulative net dealer flow as intraday lines in a sub-pane
 * below the main chart (chart.addPane(), shared time axis):
 *   C.FLOW = net dealer call flow in contracts (at-bid minus at-ask
 *            volume summed across strikes; above zero = net dealer buying)
 *   P.FLOW = same for puts
 * Dealer perspective matches the volume histograms (negated aggressor,
 * per the utils/trade_classification convention). Because the volume
 * rows are day-to-date cumulative, each window's strike-sum IS the
 * running net flow through that window: a CVD-style path per session.
 *
 * Descriptive only: where the tape's net aggression accumulated, not a
 * positioning or direction call (options-flow predictiveness is
 * validated null; see test/FINDINGS-option-aggressor-census.md).
 *
 * Data arrives pre-derived from GreeksHeatmapPlugin's onVolFlow
 * callback: { [sessionDate]: [{time, callNet, putNet}] }. One series
 * per metric per day so lines never connect across session gaps.
 *
 * The pane MUST be created with chart.addPane(true) (preserveEmptyPane):
 * empty emissions momentarily remove every series, and the library
 * otherwise auto-removes the emptied pane, detaching this class's
 * handle (addSeries then throws; 2026-08-11 crash).
 */

import { LineSeries } from 'lightweight-charts';

const METRICS = ['callNet', 'putNet'];

const SERIES_OPTIONS = {
  callNet: {
    color: '#4ade80', lineWidth: 2, title: 'C.FLOW',
  },
  putNet: {
    color: '#f87171', lineWidth: 2, title: 'P.FLOW',
  },
};

function formatContracts(value) {
  if (value == null || isNaN(value)) return '';
  if (Math.abs(value) >= 10000) return `${(value / 1000).toFixed(1)}k`;
  return value.toFixed(0);
}

export class VolFlowIndicator {
  /**
   * @param {object} chart - The main lightweight-charts IChartApi instance
   * @param {object} pane  - An IPaneApi from chart.addPane(true)
   */
  constructor(chart, pane) {
    this.chart = chart;
    this.pane = pane;
    this.enabled = false;
    this._zeroPriceLine = null;
    this._segments = { callNet: [], putNet: [] };
    this._lastSeries = null;
  }

  _removeSegments() {
    for (const key of METRICS) {
      for (const s of this._segments[key]) {
        try {
          this.chart.removeSeries(s);
        } catch (e) {
          // Series may already be detached during chart rebuild
        }
      }
      this._segments[key].length = 0;
    }
    this._zeroPriceLine = null;
  }

  _createSegment(key, isFirst) {
    return this.pane.addSeries(LineSeries, {
      priceScaleId: 'right',
      ...SERIES_OPTIONS[key],
      title: isFirst ? SERIES_OPTIONS[key].title : '',
      lastValueVisible: isFirst,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      priceFormat: {
        type: 'custom',
        formatter: formatContracts,
      },
    });
  }

  enable(seriesByDay = null) {
    if (this.enabled) {
      if (seriesByDay) this.update(seriesByDay);
      return;
    }
    this.enabled = true;
    if (seriesByDay) this.update(seriesByDay);
    console.log('[VolFlowIndicator] Enabled');
  }

  /**
   * Replace the pane content from the derived series map
   * { [sessionDate]: [{time, callNet, putNet}] }. Days render as
   * separate segments in date order.
   */
  update(seriesByDay) {
    if (!this.enabled || !seriesByDay) return;
    this._lastSeries = seriesByDay;

    const days = Object.keys(seriesByDay).sort();
    const daySegments = days
      .map((d) => {
        const rows = (seriesByDay[d] || []).filter((r) => r && r.time != null);
        return {
          callNet: rows.filter((r) => Number.isFinite(r.callNet)).map((r) => ({ time: r.time, value: r.callNet })),
          putNet: rows.filter((r) => Number.isFinite(r.putNet)).map((r) => ({ time: r.time, value: r.putNet })),
        };
      })
      .filter((seg) => seg.callNet.length || seg.putNet.length);

    const numSegments = daySegments.length;
    if (numSegments === 0) {
      // Empty emission = the data context vanished (underlying/date
      // switch, fetch failure): clear rather than hold stale series.
      this._removeSegments();
      return;
    }

    if (this._segments.callNet.length !== numSegments) {
      this._removeSegments();
      for (let i = 0; i < numSegments; i++) {
        for (const key of METRICS) {
          this._segments[key].push(this._createSegment(key, i === 0));
        }
      }
      // Zero reference: the net-flow sign boundary
      if (this._segments.callNet.length > 0) {
        this._zeroPriceLine = this._segments.callNet[0].createPriceLine({
          price: 0,
          color: '#666666',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: false,
        });
      }
      try {
        this.pane.priceScale('right').applyOptions({
          visible: true,
          borderVisible: true,
          borderColor: '#2a2a2a',
          autoScale: true,
        });
      } catch (e) {
        console.warn('[VolFlowIndicator] Price scale config error:', e);
      }
    }

    for (let i = 0; i < numSegments; i++) {
      for (const key of METRICS) {
        this._segments[key][i].setData(daySegments[i][key]);
      }
    }
  }

  disable() {
    if (!this.enabled) return;
    this._removeSegments();
    try {
      this.pane.priceScale('right').applyOptions({ visible: false });
    } catch (e) {
      // Price scale may not exist during chart teardown
    }
    this.enabled = false;
    console.log('[VolFlowIndicator] Disabled');
  }

  isEnabled() {
    return this.enabled;
  }
}

export default VolFlowIndicator;
